import React, { useEffect, useState, useRef } from "react";
import {
  BookOpen, Plus, Search, Edit, Trash2, Clock, Upload, Download,
  Save, DollarSign, Tag, AlertCircle, CheckCircle, ArrowLeft,
  FileText, HelpCircle, ChevronDown, ChevronRight, ArrowUp,
  ArrowDown, Layers, ExternalLink, Paperclip, X, Check, XCircle
} from "react-feather";
import {
  collection, getDocs, doc, addDoc, updateDoc, deleteDoc, query, orderBy
} from "firebase/firestore";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { db, storage } from "shared/utils/firebase";
import PageHeader from "shared/components/PageHeader/PageHeader";
import styles from "./CourseManagement.module.css";

const genId = () => `item_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

const parseCSVString = (text) => {
  const lines = [];
  let cur = "";
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const char = text[i];
    const next = text[i + 1];
    if (char === '"') {
      if (inQuotes && next === '"') {
        cur += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if ((char === '\n' || char === '\r') && !inQuotes) {
      if (char === '\r' && next === '\n') i++;
      if (cur.trim()) lines.push(cur);
      cur = "";
    } else {
      cur += char;
    }
  }
  if (cur.trim()) lines.push(cur);

  if (lines.length < 2) return [];

  const parseLine = (line) => {
    const fields = [];
    let field = "";
    let q = false;
    for (let i = 0; i < line.length; i++) {
      const c = line[i];
      const n = line[i + 1];
      if (c === '"') {
        if (q && n === '"') {
          field += '"';
          i++;
        } else {
          q = !q;
        }
      } else if (c === ',' && !q) {
        fields.push(field);
        field = "";
      } else {
        field += c;
      }
    }
    fields.push(field);
    return fields;
  };

  const headers = parseLine(lines[0]).map(h => h.trim());
  const rows = [];
  for (let i = 1; i < lines.length; i++) {
    const values = parseLine(lines[i]);
    const obj = {};
    headers.forEach((h, index) => {
      obj[h] = values[index] !== undefined ? values[index].trim() : "";
    });
    rows.push(obj);
  }
  return rows;
};

const CONTENT_TYPES = {
  video:    { label: "Video Lecture",   badge: "bg-primary" },
  article:  { label: "Reading Article", badge: "bg-success" },
  resource: { label: "Resource / File", badge: "bg-warning text-dark" },
  quiz:     { label: "Quiz",            badge: "bg-danger" },
};

function ContentTypeIcon({ type, size = 14 }) {
  const p = { size, className: "me-1" };
  if (type === "video")    return <Upload {...p} />;
  if (type === "article")  return <FileText {...p} />;
  if (type === "resource") return <Paperclip {...p} />;
  if (type === "quiz")     return <HelpCircle {...p} />;
  return null;
}

function CourseManagement() {
  // ── Navigation ──────────────────────────────────────────────────────────────
  const [activeView, setActiveView] = useState("catalog");
  const [activeCourse, setActiveCourse] = useState(null);
  const [workspaceTab, setWorkspaceTab] = useState("details");

  // ── Catalog ──────────────────────────────────────────────────────────────────
  const [courses, setCourses] = useState([]);
  const [filteredCourses, setFilteredCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterCategory, setFilterCategory] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [showCourseModal, setShowCourseModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [courseToDelete, setCourseToDelete] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imageProgress, setImageProgress] = useState(0);
  const [courseForm, setCourseForm] = useState({
    title: "", description: "", category: "", difficulty: "beginner",
    duration: "", price: "", instructor: "", tags: [], imageUrl: "",
    isPublished: false, learningOutcomes: ["", "", "", ""], prerequisites: ["", ""],
    siliconPoints: 50
  });

  // ── Sub-Tabs & Quiz Reset Requests ──────────────────────────────────────────
  const [activeSubTab, setActiveSubTab] = useState("courses"); // "courses" | "quizRequests"
  const [quizRequests, setQuizRequests] = useState([]);
  const [loadingRequests, setLoadingRequests] = useState(false);
  const [requestsStatus, setRequestsStatus] = useState({ message: "", type: "" });

  // ── Course CSV Import ────────────────────────────────────────────────────────
  const [showCSVUploadModal, setShowCSVUploadModal] = useState(false);
  const [courseCsvFile, setCourseCsvFile] = useState(null);
  const [uploadingCourseCSV, setUploadingCourseCSV] = useState(false);
  const [csvUploadStatus, setCsvUploadStatus] = useState({ message: "", type: "" });

  // ── Workspace ────────────────────────────────────────────────────────────────
  const [workspaceAlert, setWorkspaceAlert] = useState({ message: "", type: "" });
  const [savingDetails, setSavingDetails] = useState(false);

  // ── Sections ─────────────────────────────────────────────────────────────────
  const [sections, setSections] = useState([]);
  const [loadingSections, setLoadingSections] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});
  const [showSectionModal, setShowSectionModal] = useState(false);
  const [editingSection, setEditingSection] = useState(null);
  const [sectionForm, setSectionForm] = useState({ sectionTitle: "", sectionDescription: "" });
  const [savingSection, setSavingSection] = useState(false);
  const [showDeleteSectionConfirm, setShowDeleteSectionConfirm] = useState(false);
  const [deletingSection, setDeletingSection] = useState(null);

  // ── Content Items ────────────────────────────────────────────────────────────
  const [showItemModal, setShowItemModal] = useState(false);
  const [targetSectionId, setTargetSectionId] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [itemContentType, setItemContentType] = useState("video");
  const [itemForm, setItemForm] = useState({
    title: "", description: "", lessonUrl: "", duration: "",
    articleContent: "", fileUrl: "", fileType: "PDF Document", fileSize: "",
    quizScope: "module", passMark: 70, questions: []
  });
  const [uploadingItemFile, setUploadingItemFile] = useState(false);
  const [itemFileProgress, setItemFileProgress] = useState(0);
  const [savingItem, setSavingItem] = useState(false);
  const [newQuestion, setNewQuestion] = useState({ question: "", options: ["", "", "", ""], correctIndex: 0 });
  const [showDeleteItemConfirm, setShowDeleteItemConfirm] = useState(false);
  const [deletingItemInfo, setDeletingItemInfo] = useState({ sectionId: null, itemId: null });

  // ── CSV Content Import/Export & File Uploads ────────────────────────────
  const csvInputRef = useRef(null);
  const mdInputRef = useRef(null);
  const quizCsvInputRef = useRef(null);
  const [uploadingCSV, setUploadingCSV] = useState(false);

  useEffect(() => {
    if (activeSubTab === "quizRequests") {
      fetchQuizRequests();
    }
  }, [activeSubTab]);

  const handleMarkdownFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      setItemForm(prev => ({ ...prev, articleContent: evt.target.result }));
      showAlert("✅ Markdown content loaded from file!");
    };
    reader.readAsText(file);
  };

  const downloadQuizCSVTemplate = () => {
    const headers = ["Question", "OptionA", "OptionB", "OptionC", "OptionD", "CorrectOption"];
    const sampleRows = [
      ["What does HTML stand for?", "Hyper Text Markup Language", "High Text Machine Language", "Hyperlink Markup Language", "Home Tool Markup Language", "A"],
      ["Which CSS property controls text size?", "font-style", "text-size", "font-size", "text-style", "C"]
    ];
    const csvStr = [
      headers.join(","),
      ...sampleRows.map(r => r.map(cell => `"${(cell || "").replace(/"/g, '""')}"`).join(","))
    ].join("\n");

    const blob = new Blob([csvStr], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "quiz_questions_template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleQuizCSVUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const text = evt.target.result;
        const rows = parseCSVString(text);
        if (!rows || rows.length === 0) {
          showAlert("Invalid or empty quiz CSV file.", "error");
          if (quizCsvInputRef.current) quizCsvInputRef.current.value = "";
          return;
        }

        const parsedQuestions = [];
        rows.forEach(row => {
          const qText = row.Question || row.question || "";
          const optA = row.OptionA || row.optionA || row["Option A"] || "";
          const optB = row.OptionB || row.optionB || row["Option B"] || "";
          const optC = row.OptionC || row.optionC || row["Option C"] || "";
          const optD = row.OptionD || row.optionD || row["Option D"] || "";

          let correctStr = (row.CorrectOption || row.correctOption || row.Correct || row.correct || "A").toString().trim().toUpperCase();
          let correctIdx = 0;
          if (correctStr === "B" || correctStr === "1") correctIdx = 1;
          else if (correctStr === "C" || correctStr === "2") correctIdx = 2;
          else if (correctStr === "D" || correctStr === "3") correctIdx = 3;
          else if (correctStr === "A" || correctStr === "0") correctIdx = 0;

          if (qText && optA && optB) {
            parsedQuestions.push({
              id: genId(),
              question: qText,
              options: [optA, optB, optC || "N/A", optD || "N/A"],
              correctIndex: correctIdx
            });
          }
        });

        if (parsedQuestions.length > 0) {
          setItemForm(prev => ({
            ...prev,
            questions: [...prev.questions, ...parsedQuestions]
          }));
          showAlert(`✅ Loaded ${parsedQuestions.length} question(s) into Quiz!`);
        } else {
          showAlert("No valid questions found in CSV.", "error");
        }
      } catch (err) {
        console.error("Quiz CSV parse error:", err);
        showAlert("Error reading Quiz CSV file.", "error");
      }
      if (quizCsvInputRef.current) quizCsvInputRef.current.value = "";
    };

    reader.readAsText(file);
  };

  const downloadCSVTemplate = () => {
    const headers = [
      "SectionTitle", "SectionDescription", "ContentType", "ItemTitle",
      "ItemDescription", "LessonUrl", "Duration", "ArticleContent",
      "FileUrl", "FileType", "FileSize", "QuizScope", "PassMark"
    ];
    const sampleRows = [
      ["Module 1: Introduction", "Getting started with course", "video", "Welcome to Course", "Overview lecture", "https://youtube.com/embed/example", "10:30", "", "", "", "", "", ""],
      ["Module 1: Introduction", "Getting started with course", "article", "HTML & CSS Guide", "Complete guide article", "", "5 min", "# Welcome\nThis is a sample article content.", "", "", "", "", ""],
      ["Module 1: Introduction", "Getting started with course", "resource", "Course Slides PDF", "Download slides", "", "", "", "https://example.com/slides.pdf", "PDF Document", "2.1 MB", "", ""],
      ["Module 1: Introduction", "Getting started with course", "quiz", "Module 1 Quiz", "Check your understanding", "", "", "", "", "", "", "module", "70"]
    ];
    const csvStr = [
      headers.join(","),
      ...sampleRows.map(r => r.map(cell => `"${(cell || "").replace(/"/g, '""')}"`).join(","))
    ].join("\n");

    const blob = new Blob([csvStr], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "course_content_template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleImportCSV = (e) => {
    const file = e.target.files[0];
    if (!file || !activeCourse?.id) return;
    setUploadingCSV(true);

    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const text = event.target.result;
        const rows = parseCSVString(text);
        if (!rows || rows.length === 0) {
          showAlert("Invalid or empty CSV file.", "error");
          setUploadingCSV(false);
          if (csvInputRef.current) csvInputRef.current.value = "";
          return;
        }

        const sectionsMap = {};
        rows.forEach(row => {
          const secTitle = row.SectionTitle || "General Section";
          const secDesc = row.SectionDescription || "";
          if (!sectionsMap[secTitle]) {
            sectionsMap[secTitle] = { sectionTitle: secTitle, sectionDescription: secDesc, items: [] };
          }
          if (row.ItemTitle) {
            sectionsMap[secTitle].items.push({
              id: genId(),
              contentType: (row.ContentType || "video").toLowerCase(),
              title: row.ItemTitle,
              description: row.ItemDescription || "",
              lessonUrl: row.LessonUrl || "",
              duration: row.Duration || "",
              articleContent: row.ArticleContent || "",
              fileUrl: row.FileUrl || "",
              fileType: row.FileType || "PDF Document",
              fileSize: row.FileSize || "",
              quizScope: row.QuizScope || "module",
              passMark: parseInt(row.PassMark) || 70,
              questions: []
            });
          }
        });

        let currentSections = [...sections];
        let orderCounter = currentSections.length > 0 ? Math.max(...currentSections.map(s => s.order || 0)) : 0;

        for (const secTitle of Object.keys(sectionsMap)) {
          const secData = sectionsMap[secTitle];
          const existingIdx = currentSections.findIndex(s => s.sectionTitle.toLowerCase() === secTitle.toLowerCase());

          if (existingIdx !== -1) {
            const sec = currentSections[existingIdx];
            const curItems = sec.contentItems || [];
            let itemOrder = curItems.length > 0 ? Math.max(...curItems.map(i => i.order || 0)) : 0;
            const newItems = secData.items.map(item => ({ ...item, order: ++itemOrder }));
            const updatedItems = [...curItems, ...newItems];

            await updateDoc(doc(db, "courses", activeCourse.id, "sections", sec.id), {
              contentItems: updatedItems,
              updatedAt: new Date()
            });

            currentSections[existingIdx] = { ...sec, contentItems: updatedItems };
          } else {
            orderCounter++;
            let itemOrder = 0;
            const newItems = secData.items.map(item => ({ ...item, order: ++itemOrder }));
            const newSecDoc = {
              sectionTitle: secData.sectionTitle,
              sectionDescription: secData.sectionDescription,
              order: orderCounter,
              contentItems: newItems,
              createdAt: new Date()
            };

            const docRef = await addDoc(collection(db, "courses", activeCourse.id, "sections"), newSecDoc);
            currentSections.push({ id: docRef.id, ...newSecDoc });
          }
        }

        setSections(currentSections);
        showAlert(`✅ CSV content imported successfully! Processed ${Object.keys(sectionsMap).length} section(s).`);
      } catch (err) {
        console.error("CSV import error:", err);
        showAlert("Failed to import CSV file.", "error");
      }
      setUploadingCSV(false);
      if (csvInputRef.current) csvInputRef.current.value = "";
    };

    reader.readAsText(file);
  };

  const categories = [
    "Web Development","Data Science","Mobile Development","DevOps",
    "UI/UX Design","Cybersecurity","Machine Learning","Blockchain","Other"
  ];
  const difficulties = ["beginner","intermediate","advanced"];

  // ── Effects ──────────────────────────────────────────────────────────────────
  useEffect(() => { fetchCourses(); }, []); // eslint-disable-line
  useEffect(() => { filterCourses(); }, [courses, searchTerm, filterCategory, filterStatus]); // eslint-disable-line
  useEffect(() => { if (activeCourse) fetchSections(activeCourse.id); }, [activeCourse]); // eslint-disable-line

  const showAlert = (msg, type = "success") => {
    setWorkspaceAlert({ message: msg, type });
    setTimeout(() => setWorkspaceAlert({ message: "", type: "" }), 4000);
  };

  // ── Firestore ────────────────────────────────────────────────────────────────
  const fetchCourses = async () => {
    setLoading(true);
    try {
      const snap = await getDocs(collection(db, "courses"));
      setCourses(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const filterCourses = () => {
    let f = [...courses];
    if (searchTerm) {
      const s = searchTerm.toLowerCase();
      f = f.filter(c => c.title?.toLowerCase().includes(s) || c.category?.toLowerCase().includes(s) || c.instructor?.toLowerCase().includes(s));
    }
    if (filterCategory !== "all") f = f.filter(c => c.category === filterCategory);
    if (filterStatus !== "all") f = f.filter(c => filterStatus === "published" ? c.isPublished : !c.isPublished);
    setFilteredCourses(f);
  };

  const fetchSections = async (courseId) => {
    setLoadingSections(true);
    try {
      const q = query(collection(db, "courses", courseId, "sections"), orderBy("order", "asc"));
      const snap = await getDocs(q);
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setSections(list);
      if (list.length > 0) setExpandedSections({ [list[0].id]: true });
    } catch (e) { console.error(e); }
    setLoadingSections(false);
  };

  const handleOpenWorkspace = (course) => {
    setActiveCourse(course);
    setCourseForm({
      title: course.title || "", description: course.description || "",
      category: course.category || "", difficulty: course.difficulty || "beginner",
      duration: course.duration || "", price: course.price || "",
      instructor: course.instructor || "", tags: course.tags || [],
      imageUrl: course.imageUrl || "", isPublished: course.isPublished || false,
      learningOutcomes: course.learningOutcomes || ["", "", "", ""],
      prerequisites: course.prerequisites || ["", ""],
      siliconPoints: course.siliconPoints !== undefined ? Number(course.siliconPoints) : 50
    });
    setWorkspaceTab("details");
    setSections([]);
    setExpandedSections({});
    setActiveView("workspace");
  };

  const handleBackToCatalog = () => {
    setActiveView("catalog");
    setActiveCourse(null);
    setSections([]);
    setWorkspaceTab("details");
    fetchCourses();
  };

  const handleSaveCourseDetails = async () => {
    if (!activeCourse) return;
    setSavingDetails(true);
    try {
      const payload = { ...courseForm, siliconPoints: Number(courseForm.siliconPoints) || 50, updatedAt: new Date() };
      await updateDoc(doc(db, "courses", activeCourse.id), payload);
      setActiveCourse(prev => ({ ...prev, ...payload }));
      showAlert("Course details saved.");
    } catch (e) { showAlert("Error saving.", "error"); }
    setSavingDetails(false);
  };

  const handleCourseImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file || !activeCourse?.id) return;
    setUploadingImage(true);
    setImageProgress(0);

    const storageRef = ref(storage, `images/courses_thumbnails/${activeCourse.id}_${Date.now()}_${file.name.replace(/\s+/g, "_")}`);
    const task = uploadBytesResumable(storageRef, file);

    task.on(
      "state_changed",
      (snap) => setImageProgress(Math.round((snap.bytesTransferred / snap.totalBytes) * 100)),
      (err) => {
        console.error("Upload error:", err.code, err.message);
        setUploadingImage(false);
        setImageProgress(0);
        showAlert(`Upload failed: ${err.code || err.message}`, "error");
      },
      async () => {
        const url = await getDownloadURL(task.snapshot.ref);
        // Update local state immediately
        setCourseForm(prev => ({ ...prev, imageUrl: url }));
        setActiveCourse(prev => ({ ...prev, imageUrl: url }));
        setCourses(prev => prev.map(c => c.id === activeCourse.id ? { ...c, imageUrl: url } : c));
        // Persist to Firestore
        try {
          await updateDoc(doc(db, "courses", activeCourse.id), { imageUrl: url, updatedAt: new Date() });
          showAlert("✅ Image uploaded successfully!");
        } catch (err) {
          console.error("Firestore save error:", err);
          showAlert("Uploaded but failed to save URL.", "error");
        }
        setUploadingImage(false);
        setImageProgress(0);
      }
    );
  };

  const handleSaveNewCourse = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...courseForm, siliconPoints: Number(courseForm.siliconPoints) || 50, createdAt: new Date(), updatedAt: new Date() };
      const r = await addDoc(collection(db, "courses"), payload);
      setShowCourseModal(false);
      handleOpenWorkspace({ id: r.id, ...payload });
    } catch (e) { console.error(e); }
  };

  const handleDeleteCourse = async () => {
    if (!courseToDelete) return;
    try {
      await deleteDoc(doc(db, "courses", courseToDelete.id));
      setCourses(prev => prev.filter(c => c.id !== courseToDelete.id));
      setShowDeleteModal(false);
    } catch (e) { console.error(e); }
  };

  const handleSaveSection = async (e) => {
    e.preventDefault();
    setSavingSection(true);
    try {
      if (editingSection) {
        await updateDoc(doc(db, "courses", activeCourse.id, "sections", editingSection.id), { ...sectionForm, updatedAt: new Date() });
        setSections(prev => prev.map(s => s.id === editingSection.id ? { ...s, ...sectionForm } : s));
        showAlert("Section updated.");
      } else {
        const order = sections.length > 0 ? Math.max(...sections.map(s => s.order || 0)) + 1 : 1;
        const r = await addDoc(collection(db, "courses", activeCourse.id, "sections"), { ...sectionForm, order, contentItems: [], createdAt: new Date() });
        const ns = { id: r.id, ...sectionForm, order, contentItems: [] };
        setSections(prev => [...prev, ns]);
        setExpandedSections(prev => ({ ...prev, [r.id]: true }));
        showAlert("Section added.");
      }
      setShowSectionModal(false);
    } catch (e) { showAlert("Error.", "error"); }
    setSavingSection(false);
  };

  const handleDeleteSectionConfirm = async () => {
    if (!deletingSection) return;
    try {
      await deleteDoc(doc(db, "courses", activeCourse.id, "sections", deletingSection.id));
      setSections(prev => prev.filter(s => s.id !== deletingSection.id));
      setShowDeleteSectionConfirm(false);
      showAlert("Section deleted.");
    } catch (e) { showAlert("Error.", "error"); }
  };

  const handleMoveSection = async (id, dir) => {
    const sorted = [...sections].sort((a, b) => (a.order || 0) - (b.order || 0));
    const idx = sorted.findIndex(s => s.id === id);
    if (idx === -1 || (dir === "up" && idx === 0) || (dir === "down" && idx === sorted.length - 1)) return;
    const swapIdx = dir === "up" ? idx - 1 : idx + 1;
    const [aO, bO] = [sorted[idx].order, sorted[swapIdx].order];
    try {
      await updateDoc(doc(db, "courses", activeCourse.id, "sections", sorted[idx].id), { order: bO });
      await updateDoc(doc(db, "courses", activeCourse.id, "sections", sorted[swapIdx].id), { order: aO });
      setSections(prev => prev.map(s => {
        if (s.id === sorted[idx].id) return { ...s, order: bO };
        if (s.id === sorted[swapIdx].id) return { ...s, order: aO };
        return s;
      }));
    } catch (e) { console.error(e); }
  };

  const resetItemForm = () => {
    setItemForm({ title: "", description: "", lessonUrl: "", duration: "", articleContent: "", fileUrl: "", fileType: "PDF Document", fileSize: "", quizScope: "module", passMark: 70, questions: [] });
    setNewQuestion({ question: "", options: ["", "", "", ""], correctIndex: 0 });
    setItemContentType("video");
  };

  const openAddItem = (sectionId) => {
    setTargetSectionId(sectionId);
    setEditingItem(null);
    resetItemForm();
    setShowItemModal(true);
  };

  const openEditItem = (sectionId, item) => {
    setTargetSectionId(sectionId);
    setEditingItem(item);
    setItemContentType(item.contentType);
    setItemForm({
      title: item.title || "", description: item.description || "",
      lessonUrl: item.lessonUrl || "", duration: item.duration || "",
      articleContent: item.articleContent || "",
      fileUrl: item.fileUrl || "", fileType: item.fileType || "PDF Document", fileSize: item.fileSize || "",
      quizScope: item.quizScope || "module", passMark: item.passMark || 70, questions: item.questions || []
    });
    setNewQuestion({ question: "", options: ["", "", "", ""], correctIndex: 0 });
    setShowItemModal(true);
  };

  const cleanItemData = (data) => {
    return {
      contentType: itemContentType || "video",
      title: data.title || "",
      description: data.description || "",
      lessonUrl: data.lessonUrl || "",
      duration: data.duration || "",
      articleContent: data.articleContent || "",
      fileUrl: data.fileUrl || "",
      fileType: data.fileType || "PDF Document",
      fileSize: data.fileSize || "",
      quizScope: data.quizScope || "module",
      passMark: typeof data.passMark === "number" ? data.passMark : 70,
      questions: Array.isArray(data.questions) ? data.questions.map(q => ({
        id: q.id || genId(),
        question: q.question || "",
        options: Array.isArray(q.options) ? q.options.map(o => o || "") : ["", "", "", ""],
        correctIndex: typeof q.correctIndex === "number" ? q.correctIndex : 0
      })) : []
    };
  };

  const handleSaveContentItem = async (e) => {
    e.preventDefault();
    if (!targetSectionId) return;
    setSavingItem(true);
    const section = sections.find(s => s.id === targetSectionId);
    if (!section) { setSavingItem(false); return; }
    
    const cur = section.contentItems || [];
    const cleanForm = cleanItemData(itemForm);
    let updated;

    if (editingItem) {
      updated = cur.map(i => i.id === editingItem.id ? { ...i, order: i.order || 1, ...cleanForm } : i);
    } else {
      const order = cur.length > 0 ? Math.max(...cur.map(i => i.order || 0)) + 1 : 1;
      updated = [...cur, { id: genId(), order, ...cleanForm }];
    }

    try {
      await updateDoc(doc(db, "courses", activeCourse.id, "sections", targetSectionId), {
        contentItems: updated,
        updatedAt: new Date()
      });
      setSections(prev => prev.map(s => s.id === targetSectionId ? { ...s, contentItems: updated } : s));
      setShowItemModal(false);
      showAlert(editingItem ? "✅ Item updated successfully!" : "✅ Item added successfully!");
    } catch (e) {
      console.error("Save content item error:", e);
      showAlert("Error saving item to database: " + (e.message || "Unknown error"), "error");
    }
    setSavingItem(false);
  };

  const handleConfirmDeleteItem = async () => {
    const { sectionId, itemId } = deletingItemInfo;
    if (!sectionId || !itemId) return;
    const section = sections.find(s => s.id === sectionId);
    if (!section) return;
    const updated = (section.contentItems || []).filter(i => i.id !== itemId);
    try {
      await updateDoc(doc(db, "courses", activeCourse.id, "sections", sectionId), { contentItems: updated, updatedAt: new Date() });
      setSections(prev => prev.map(s => s.id === sectionId ? { ...s, contentItems: updated } : s));
      setShowDeleteItemConfirm(false);
      showAlert("Item deleted.");
    } catch (e) { showAlert("Error.", "error"); }
  };

  const handleMoveItem = async (sectionId, itemId, dir) => {
    const section = sections.find(s => s.id === sectionId);
    if (!section) return;
    const items = [...(section.contentItems || [])].sort((a, b) => (a.order || 0) - (b.order || 0));
    const idx = items.findIndex(i => i.id === itemId);
    if (idx === -1 || (dir === "up" && idx === 0) || (dir === "down" && idx === items.length - 1)) return;
    const swapIdx = dir === "up" ? idx - 1 : idx + 1;
    const tmp = items[idx].order;
    items[idx] = { ...items[idx], order: items[swapIdx].order };
    items[swapIdx] = { ...items[swapIdx], order: tmp };
    try {
      await updateDoc(doc(db, "courses", activeCourse.id, "sections", sectionId), { contentItems: items, updatedAt: new Date() });
      setSections(prev => prev.map(s => s.id === sectionId ? { ...s, contentItems: items } : s));
    } catch (e) { console.error(e); }
  };

  const handleAddQuestion = () => {
    if (!newQuestion.question.trim() || newQuestion.options.some(o => !o.trim())) return;
    setItemForm(prev => ({ ...prev, questions: [...prev.questions, { ...newQuestion, id: genId() }] }));
    setNewQuestion({ question: "", options: ["", "", "", ""], correctIndex: 0 });
  };

  const handleItemFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file || !activeCourse?.id) return;
    setUploadingItemFile(true);
    setItemFileProgress(0);

    const fileName = `${Date.now()}_${file.name.replace(/\s+/g, "_")}`;
    const storageRef = ref(storage, `courses_resources/${activeCourse.id}/${fileName}`);
    const task = uploadBytesResumable(storageRef, file);

    task.on(
      "state_changed",
      (snap) => setItemFileProgress(Math.round((snap.bytesTransferred / snap.totalBytes) * 100)),
      (err) => {
        console.error("Resource file upload error:", err);
        setUploadingItemFile(false);
        setItemFileProgress(0);
        showAlert(`File upload failed: ${err.code || err.message}`, "error");
      },
      async () => {
        try {
          const url = await getDownloadURL(task.snapshot.ref);
          const sizeStr = `${(file.size / (1024 * 1024)).toFixed(2)} MB`;
          setItemForm(prev => ({
            ...prev,
            fileUrl: url,
            fileSize: sizeStr
          }));
          showAlert("✅ File uploaded successfully!");
        } catch (err) {
          console.error("Get download URL error:", err);
          showAlert("Failed to retrieve file download URL.", "error");
        }
        setUploadingItemFile(false);
        setItemFileProgress(0);
      }
    );
  };

  const fetchQuizRequests = async () => {
    setLoadingRequests(true);
    try {
      const snap = await getDocs(collection(db, "quizAttemptRequests"));
      setQuizRequests(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) { console.error(e); }
    setLoadingRequests(false);
  };

  const handleApproveRequest = async (id) => {
    try {
      await updateDoc(doc(db, "quizAttemptRequests", id), { status: "approved", updatedAt: new Date() });
      setRequestsStatus({ message: "Approved.", type: "success" });
      fetchQuizRequests();
      setTimeout(() => setRequestsStatus({ message: "", type: "" }), 4000);
    } catch (e) { console.error(e); }
  };

  const handleRejectRequest = async (id) => {
    try {
      await updateDoc(doc(db, "quizAttemptRequests", id), { status: "rejected", updatedAt: new Date() });
      setRequestsStatus({ message: "Rejected.", type: "success" });
      fetchQuizRequests();
      setTimeout(() => setRequestsStatus({ message: "", type: "" }), 4000);
    } catch (e) { console.error(e); }
  };

  const exportCourses = () => {
    const csv = [
      ["Title","Category","Difficulty","Duration","Price","Instructor","Status"].join(","),
      ...filteredCourses.map(c => [`"${c.title}"`,`"${c.category}"`,`"${c.difficulty}"`,`"${c.duration}"`,`"${c.price}"`,`"${c.instructor}"`,`"${c.isPublished?"Published":"Draft"}"`].join(","))
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "courses.csv"; a.click();
    window.URL.revokeObjectURL(url);
  };

  const downloadCourseCSVTemplate = () => {
    const template = `title,description,category,difficulty,duration,price,instructor,tags,isPublished,siliconPoints,learningOutcomes,prerequisites
"Embedded C Masterclass","Learn bare-metal C programming for ARM microcontrollers from scratch","Embedded Systems & IoT","intermediate","25 Hours","₹499","Silicon Logix Team","c;embedded;arm","true",100,"Master C pointers & bitwise math;Write bare-metal peripheral drivers;Understand interrupts & timers;Debug with GDB","Basic C knowledge;Digital Electronics"
"VLSI System Design","Comprehensive guide to ASIC/FPGA digital design using Verilog","VLSI Design","advanced","40 Hours","₹999","Silicon Logix Team","vlsi;verilog;fpga","true",150,"Verilog HDL modeling;Synthesis & STA constraints;FPGA prototyping","Digital Logic Design"`;
    const blob = new Blob([template], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "course_import_template.csv";
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleCourseCSVUpload = async (e) => {
    e.preventDefault();
    if (!courseCsvFile) return;

    setUploadingCourseCSV(true);
    setCsvUploadStatus({ message: "Parsing and uploading course CSV...", type: "info" });

    try {
      const text = await courseCsvFile.text();
      const parsedRows = parseCSVString(text);

      if (!parsedRows || parsedRows.length === 0) {
        setCsvUploadStatus({ message: "No valid rows found in CSV file.", type: "error" });
        setUploadingCourseCSV(false);
        return;
      }

      let successCount = 0;
      let errorCount = 0;

      for (let row of parsedRows) {
        try {
          const title = row.title || row.Title || "";
          if (!title) continue;

          const category = row.category || row.Category || "General Tech";
          const difficulty = (row.difficulty || row.Difficulty || "beginner").toLowerCase();
          const duration = row.duration || row.Duration || "";
          const price = row.price || row.Price || "";
          const instructor = row.instructor || row.Instructor || "Silicon Logix Team";
          const description = row.description || row.Description || "";

          const tagsStr = row.tags || row.Tags || "";
          const tags = tagsStr ? tagsStr.split(";").map(t => t.trim()).filter(Boolean) : [];

          const isPublished = (row.isPublished || row.ispublished || "").toString().toLowerCase() === "true";
          const siliconPoints = Number(row.siliconPoints || row.siliconpoints || row.points) || 50;

          const outcomesStr = row.learningOutcomes || row.learningoutcomes || row.outcomes || "";
          const learningOutcomes = outcomesStr ? outcomesStr.split(";").map(o => o.trim()).filter(Boolean) : ["", "", "", ""];

          const prereqStr = row.prerequisites || row.Prerequisites || "";
          const prerequisites = prereqStr ? prereqStr.split(";").map(p => p.trim()).filter(Boolean) : ["", ""];

          const courseData = {
            title,
            description,
            category,
            difficulty,
            duration,
            price,
            instructor,
            tags,
            isPublished,
            siliconPoints,
            learningOutcomes,
            prerequisites,
            createdAt: new Date(),
            updatedAt: new Date()
          };

          await addDoc(collection(db, "courses"), courseData);
          successCount++;
        } catch (err) {
          console.error("Error inserting course CSV row:", err);
          errorCount++;
        }
      }

      if (errorCount > 0) {
        setCsvUploadStatus({
          message: `Uploaded ${successCount} courses with ${errorCount} errors.`,
          type: "warning"
        });
      } else {
        setCsvUploadStatus({
          message: `🎉 Successfully imported ${successCount} course(s)!`,
          type: "success"
        });
      }

      fetchCourses();
      setTimeout(() => {
        setShowCSVUploadModal(false);
        setCourseCsvFile(null);
        setCsvUploadStatus({ message: "", type: "" });
      }, 2500);
    } catch (error) {
      console.error("Error reading CSV:", error);
      setCsvUploadStatus({ message: "Failed to read CSV file.", type: "error" });
    }
    setUploadingCourseCSV(false);
  };

  const getDiffColor = (d) => ({ beginner:"bg-success", intermediate:"bg-warning", advanced:"bg-danger" }[d] || "bg-secondary");
  const getStatColor = (p) => p ? "bg-success" : "bg-warning";

  const sortedSections = [...sections].sort((a, b) => (a.order || 0) - (b.order || 0));
  const totalItems = sections.reduce((acc, s) => acc + (s.contentItems?.length || 0), 0);
  const totalVideos = sections.reduce((acc, s) => acc + (s.contentItems?.filter(i => i.contentType === "video").length || 0), 0);
  const totalQuizzes = sections.reduce((acc, s) => acc + (s.contentItems?.filter(i => i.contentType === "quiz").length || 0), 0);

  // ── Loading ──────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div>
        <p className="mt-2">Loading course catalog...</p>
      </div>
    );
  }

  // ── WORKSPACE VIEW ───────────────────────────────────────────────────────────
  if (activeView === "workspace" && activeCourse) {
    return (
      <div className={styles.workspaceContainer}>

        {/* Header */}
        <div className={styles.workspaceTopHeader}>
          <div className={styles.headerBackArea}>
            <button className={styles.backBtn} onClick={handleBackToCatalog}>
              <ArrowLeft size={18} className="me-2" />Back to Course Catalog
            </button>
            <div className={styles.courseMetaHeader}>
              <h3 className={styles.workspaceCourseTitle}>{activeCourse.title}</h3>
              <div className={styles.courseBadgeRow}>
                <span className={`badge ${getStatColor(activeCourse.isPublished)} me-2`}>{activeCourse.isPublished ? "Published" : "Draft"}</span>
                <span className={styles.metaBadge}>{activeCourse.category}</span>
                <span className={styles.metaBadge}>{activeCourse.difficulty}</span>
                {activeCourse.price && <span className={styles.metaBadgePrice}>{String(activeCourse.price).toLowerCase().includes('free') ? activeCourse.price : `₹${String(activeCourse.price).replace(/[\$₹]/g, '').trim()}`}</span>}
              </div>
            </div>
          </div>
          <div className={styles.headerActionsArea}>
            <button className={styles.quickSaveBtn} onClick={handleSaveCourseDetails} disabled={savingDetails}>
              <Save size={16} className="me-2" />{savingDetails ? "Saving..." : "Save Course"}
            </button>
          </div>
        </div>

        {/* Alert */}
        {workspaceAlert.message && (
          <div className={`alert ${workspaceAlert.type === "error" ? "alert-danger" : "alert-success"} mx-3 my-2`}>
            {workspaceAlert.type === "error" ? <AlertCircle size={16} className="me-2" /> : <CheckCircle size={16} className="me-2" />}
            {workspaceAlert.message}
          </div>
        )}

        {/* Tabs */}
        <div className={styles.workspaceTabsBar}>
          <button className={`${styles.tabBtn} ${workspaceTab === "details" ? styles.activeTab : ""}`} onClick={() => setWorkspaceTab("details")}>
            <BookOpen size={18} className="me-2" />1. Details &amp; Meta
          </button>
          <button className={`${styles.tabBtn} ${workspaceTab === "content" ? styles.activeTab : ""}`} onClick={() => { setWorkspaceTab("content"); if (sections.length === 0 && activeCourse) fetchSections(activeCourse.id); }}>
            <Layers size={18} className="me-2" />2. Course Content ({totalItems} items)
          </button>
        </div>

        <div className={styles.workspaceTabContent}>

          {/* ═══ TAB: DETAILS ═══ */}
          {workspaceTab === "details" && (
            <div className={styles.tabPanel}>
              <div className={styles.sectionCard}>
                <h5 className={styles.sectionCardTitle}>Basic Information</h5>
                <div className="row g-3">
                  <div className="col-md-8">
                    <label className="form-label fw-semibold">Course Title *</label>
                    <input type="text" className="form-control" value={courseForm.title} onChange={e => setCourseForm(p => ({ ...p, title: e.target.value }))} />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label fw-semibold">Instructor</label>
                    <input type="text" className="form-control" value={courseForm.instructor} onChange={e => setCourseForm(p => ({ ...p, instructor: e.target.value }))} />
                  </div>
                  <div className="col-12">
                    <label className="form-label fw-semibold">Description</label>
                    <textarea className="form-control" rows={4} value={courseForm.description} onChange={e => setCourseForm(p => ({ ...p, description: e.target.value }))} />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label fw-semibold">Category</label>
                    <select className="form-select" value={courseForm.category} onChange={e => setCourseForm(p => ({ ...p, category: e.target.value }))}>
                      <option value="">Select Category</option>
                      {categories.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div className="col-md-4">
                    <label className="form-label fw-semibold">Difficulty</label>
                    <select className="form-select" value={courseForm.difficulty} onChange={e => setCourseForm(p => ({ ...p, difficulty: e.target.value }))}>
                      {difficulties.map(d => <option key={d} value={d}>{d.toUpperCase()}</option>)}
                    </select>
                  </div>
                  <div className="col-md-2">
                    <label className="form-label fw-semibold">Duration</label>
                    <input type="text" className="form-control" placeholder="10 weeks" value={courseForm.duration} onChange={e => setCourseForm(p => ({ ...p, duration: e.target.value }))} />
                  </div>
                  <div className="col-md-2">
                    <label className="form-label fw-semibold">Price</label>
                    <input type="text" className="form-control" placeholder="₹199" value={courseForm.price} onChange={e => setCourseForm(p => ({ ...p, price: e.target.value }))} />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label fw-semibold">Silicon-Point Value</label>
                    <input type="number" min="0" className="form-control" placeholder="50" value={courseForm.siliconPoints} onChange={e => setCourseForm(p => ({ ...p, siliconPoints: e.target.value }))} />
                  </div>
                  <div className="col-12 d-flex align-items-center gap-3">
                    <label className="form-label mb-0 fw-semibold">Status</label>
                    <div className="form-check form-switch mb-0">
                      <input type="checkbox" className="form-check-input" id="pubToggle" checked={courseForm.isPublished} onChange={e => setCourseForm(p => ({ ...p, isPublished: e.target.checked }))} />
                      <label className="form-check-label" htmlFor="pubToggle">{courseForm.isPublished ? "Published" : "Draft"}</label>
                    </div>
                  </div>
                </div>
              </div>

              <div className={styles.sectionCard}>
                <h5 className={styles.sectionCardTitle}>Course Thumbnail</h5>
                <div className="row g-3 align-items-center">
                  <div className="col-md-4">
                    {courseForm.imageUrl
                      ? <img src={courseForm.imageUrl} alt="thumb" className={styles.thumbnailPreview} />
                      : <div className={styles.thumbnailPlaceholder}>No Image</div>}
                  </div>
                  <div className="col-md-8">
                    <label className="form-label fw-semibold">Upload Image</label>
                    <input type="file" accept="image/*" className="form-control mb-2" onChange={handleCourseImageUpload} />
                    {uploadingImage && <div className={styles.progressBar}><div className={styles.progressBarFill} style={{ width: `${imageProgress}%` }}></div></div>}
                    <label className="form-label fw-semibold">Or Image URL</label>
                    <input type="text" className="form-control" placeholder="https://..." value={courseForm.imageUrl} onChange={e => setCourseForm(p => ({ ...p, imageUrl: e.target.value }))} />
                  </div>
                </div>
              </div>

              <div className={styles.sectionCard}>
                <h5 className={styles.sectionCardTitle}>Learning Outcomes</h5>
                {(courseForm.learningOutcomes || []).map((o, i) => (
                  <div key={i} className="input-group mb-2">
                    <span className="input-group-text">{i + 1}</span>
                    <input type="text" className="form-control" placeholder={`Outcome ${i + 1}`} value={o} onChange={e => { const u = [...(courseForm.learningOutcomes || [])]; u[i] = e.target.value; setCourseForm(p => ({ ...p, learningOutcomes: u })); }} />
                    {(courseForm.learningOutcomes || []).length > 1 && <button type="button" className="btn btn-outline-danger" onClick={() => setCourseForm(p => ({ ...p, learningOutcomes: p.learningOutcomes.filter((_, x) => x !== i) }))}><X size={14} /></button>}
                  </div>
                ))}
                <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setCourseForm(p => ({ ...p, learningOutcomes: [...(p.learningOutcomes || []), ""] }))}><Plus size={14} className="me-1" />Add Outcome</button>
              </div>

              <div className={styles.sectionCard}>
                <h5 className={styles.sectionCardTitle}>Prerequisites</h5>
                {(courseForm.prerequisites || []).map((pr, i) => (
                  <div key={i} className="input-group mb-2">
                    <span className="input-group-text">{i + 1}</span>
                    <input type="text" className="form-control" placeholder={`Prerequisite ${i + 1}`} value={pr} onChange={e => { const u = [...(courseForm.prerequisites || [])]; u[i] = e.target.value; setCourseForm(p => ({ ...p, prerequisites: u })); }} />
                    {(courseForm.prerequisites || []).length > 1 && <button type="button" className="btn btn-outline-danger" onClick={() => setCourseForm(p => ({ ...p, prerequisites: p.prerequisites.filter((_, x) => x !== i) }))}><X size={14} /></button>}
                  </div>
                ))}
                <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setCourseForm(p => ({ ...p, prerequisites: [...(p.prerequisites || []), ""] }))}><Plus size={14} className="me-1" />Add Prerequisite</button>
              </div>

              <div className="d-flex justify-content-end mt-3 pb-3">
                <button className="btn btn-primary px-4" onClick={handleSaveCourseDetails} disabled={savingDetails}>
                  <Save size={16} className="me-2" />{savingDetails ? "Saving..." : "Save All Details"}
                </button>
              </div>
            </div>
          )}

          {/* ═══ TAB: COURSE CONTENT ═══ */}
          {workspaceTab === "content" && (
            <div className={styles.tabPanel}>
              <div className={styles.contentStatsRow}>
                <div className={styles.contentStat}><strong>{sortedSections.length}</strong> Sections</div>
                <div className={styles.contentStat}><strong>{totalItems}</strong> Items</div>
                <div className={styles.contentStat}><strong>{totalVideos}</strong> Videos</div>
                <div className={styles.contentStat}><strong>{totalQuizzes}</strong> Quizzes</div>
                <div className="ms-auto d-flex gap-2 align-items-center">
                  <button className="btn btn-outline-secondary btn-sm" onClick={downloadCSVTemplate} title="Download CSV Template">
                    <Download size={14} className="me-1" />CSV Template
                  </button>
                  <button className="btn btn-outline-success btn-sm" onClick={() => csvInputRef.current?.click()} disabled={uploadingCSV} title="Import CSV Content">
                    <Upload size={14} className="me-1" />{uploadingCSV ? "Importing..." : "Import CSV"}
                  </button>
                  <input type="file" ref={csvInputRef} accept=".csv" style={{ display: "none" }} onChange={handleImportCSV} />
                  <button className="btn btn-primary btn-sm" onClick={() => { setEditingSection(null); setSectionForm({ sectionTitle: "", sectionDescription: "" }); setShowSectionModal(true); }}>
                    <Plus size={14} className="me-1" />Add Section
                  </button>
                </div>
              </div>

              {loadingSections ? (
                <div className="text-center py-5"><div className="spinner-border text-primary" role="status"></div></div>
              ) : sortedSections.length === 0 ? (
                <div className={styles.emptyContentState}>
                  <Layers size={48} className="text-muted mb-3" />
                  <h5>No sections yet</h5>
                  <p className="text-muted">Create sections manually or bulk import content using a CSV file.</p>
                  <div className="d-flex gap-2 justify-content-center flex-wrap">
                    <button className="btn btn-primary" onClick={() => { setEditingSection(null); setSectionForm({ sectionTitle: "", sectionDescription: "" }); setShowSectionModal(true); }}>
                      <Plus size={16} className="me-2" />Add First Section
                    </button>
                    <button className="btn btn-outline-secondary" onClick={downloadCSVTemplate}>
                      <Download size={16} className="me-2" />CSV Template
                    </button>
                    <button className="btn btn-outline-success" onClick={() => csvInputRef.current?.click()} disabled={uploadingCSV}>
                      <Upload size={16} className="me-2" />{uploadingCSV ? "Importing..." : "Import CSV"}
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  {sortedSections.map((section, secIdx) => {
                    const isExp = !!expandedSections[section.id];
                    const sortedItems = [...(section.contentItems || [])].sort((a, b) => (a.order || 0) - (b.order || 0));
                    return (
                      <div key={section.id} className={styles.sectionBlock}>
                        <div className={styles.sectionBlockHeader}>
                          <div className="d-flex align-items-center gap-2 flex-grow-1 overflow-hidden">
                            <button className={styles.sectionToggleBtn} onClick={() => setExpandedSections(p => ({ ...p, [section.id]: !p[section.id] }))}>
                              {isExp ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                            </button>
                            <span className={styles.sectionTitleText}>{section.sectionTitle}</span>
                            {section.sectionDescription && <span className={styles.sectionSubtitle}> — {section.sectionDescription}</span>}
                            <span className="badge bg-secondary flex-shrink-0">{sortedItems.length}</span>
                          </div>
                          <div className="d-flex gap-1 flex-shrink-0">
                            <button className="btn btn-sm btn-outline-secondary py-0 px-1" onClick={() => handleMoveSection(section.id, "up")} disabled={secIdx === 0}><ArrowUp size={13} /></button>
                            <button className="btn btn-sm btn-outline-secondary py-0 px-1" onClick={() => handleMoveSection(section.id, "down")} disabled={secIdx === sortedSections.length - 1}><ArrowDown size={13} /></button>
                            <button className="btn btn-sm btn-outline-primary py-0 px-2" onClick={() => { setEditingSection(section); setSectionForm({ sectionTitle: section.sectionTitle, sectionDescription: section.sectionDescription || "" }); setShowSectionModal(true); }}><Edit size={13} /></button>
                            <button className="btn btn-sm btn-outline-danger py-0 px-2" onClick={() => { setDeletingSection(section); setShowDeleteSectionConfirm(true); }}><Trash2 size={13} /></button>
                            <button className="btn btn-sm btn-primary py-0 px-2" onClick={() => openAddItem(section.id)}><Plus size={13} className="me-1" />Add Item</button>
                          </div>
                        </div>

                        {isExp && (
                          <div className={styles.sectionItemsList}>
                            {sortedItems.length === 0 ? (
                              <div className={styles.sectionEmptyItems}>
                                <p className="text-muted small mb-2">No items yet.</p>
                                <button className="btn btn-sm btn-outline-primary" onClick={() => openAddItem(section.id)}><Plus size={13} className="me-1" />Add First Item</button>
                              </div>
                            ) : (
                              <>
                                {sortedItems.map((item, itemIdx) => {
                                  const tc = CONTENT_TYPES[item.contentType] || CONTENT_TYPES.video;
                                  return (
                                    <div key={item.id} className={styles.contentItemRow}>
                                      <div className={styles.contentItemLeft}>
                                        <div className={styles.itemOrderBtns}>
                                          <button className="btn btn-sm btn-light py-0 px-1" onClick={() => handleMoveItem(section.id, item.id, "up")} disabled={itemIdx === 0}><ArrowUp size={11} /></button>
                                          <button className="btn btn-sm btn-light py-0 px-1" onClick={() => handleMoveItem(section.id, item.id, "down")} disabled={itemIdx === sortedItems.length - 1}><ArrowDown size={11} /></button>
                                        </div>
                                        <span className={`badge ${tc.badge} flex-shrink-0`}><ContentTypeIcon type={item.contentType} size={11} />{tc.label}</span>
                                        <div className={styles.contentItemInfo}>
                                          <span className={styles.contentItemTitle}>{item.title}</span>
                                          <div className={styles.contentItemMetas}>
                                            {item.duration && <span className={styles.contentItemMeta}><Clock size={11} className="me-1" />{item.duration}</span>}
                                            {item.contentType === "resource" && item.fileType && <span className={styles.contentItemMeta}>{item.fileType}</span>}
                                            {item.contentType === "resource" && item.fileSize && <span className={styles.contentItemMeta}>{item.fileSize}</span>}
                                            {item.contentType === "quiz" && <span className={`badge ${item.quizScope === "course" ? "bg-dark" : "bg-info text-dark"} ms-1 ${styles.quizScopeBadge}`}>{item.quizScope === "course" ? "Course Quiz" : "Module Quiz"}</span>}
                                            {item.contentType === "quiz" && item.questions?.length > 0 && <span className={styles.contentItemMeta}>{item.questions.length} Qs</span>}
                                            {item.contentType === "article" && item.articleContent && <span className={styles.contentItemMeta}>{Math.ceil(item.articleContent.split(" ").length / 200)} min read</span>}
                                          </div>
                                        </div>
                                      </div>
                                      <div className="d-flex gap-1 flex-shrink-0">
                                        {item.lessonUrl && <a href={item.lessonUrl} target="_blank" rel="noreferrer" className="btn btn-sm btn-outline-secondary py-0 px-2"><ExternalLink size={12} /></a>}
                                        {item.fileUrl && item.contentType === "resource" && <a href={item.fileUrl} target="_blank" rel="noreferrer" className="btn btn-sm btn-outline-secondary py-0 px-2"><Download size={12} /></a>}
                                        <button className="btn btn-sm btn-outline-primary py-0 px-2" onClick={() => openEditItem(section.id, item)}><Edit size={12} /> Edit</button>
                                        <button className="btn btn-sm btn-outline-danger py-0 px-2" onClick={() => { setDeletingItemInfo({ sectionId: section.id, itemId: item.id }); setShowDeleteItemConfirm(true); }}><Trash2 size={12} /></button>
                                      </div>
                                    </div>
                                  );
                                })}
                                <div className={styles.addItemFooter}>
                                  <button className="btn btn-sm btn-outline-primary" onClick={() => openAddItem(section.id)}><Plus size={13} className="me-1" />Add Content Item</button>
                                </div>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  <div className="text-center mt-4 pb-4">
                    <button className="btn btn-outline-primary" onClick={() => { setEditingSection(null); setSectionForm({ sectionTitle: "", sectionDescription: "" }); setShowSectionModal(true); }}>
                      <Plus size={16} className="me-2" />Add New Section
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Section Modal */}
        {showSectionModal && (
          <div className={`modal show d-block ${styles.modalOverlay}`} tabIndex="-1">
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">{editingSection ? "Edit Section" : "Add New Section"}</h5>
                  <button type="button" className="btn-close" onClick={() => setShowSectionModal(false)}></button>
                </div>
                <form onSubmit={handleSaveSection}>
                  <div className="modal-body">
                    <div className="mb-3">
                      <label className="form-label fw-semibold">Section Title *</label>
                      <input type="text" className="form-control" required value={sectionForm.sectionTitle} onChange={e => setSectionForm(p => ({ ...p, sectionTitle: e.target.value }))} />
                    </div>
                    <div className="mb-3">
                      <label className="form-label fw-semibold">Description</label>
                      <textarea className="form-control" rows={2} value={sectionForm.sectionDescription} onChange={e => setSectionForm(p => ({ ...p, sectionDescription: e.target.value }))} />
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn btn-secondary" onClick={() => setShowSectionModal(false)}>Cancel</button>
                    <button type="submit" className="btn btn-primary" disabled={savingSection}>{savingSection ? "Saving..." : editingSection ? "Update" : "Add Section"}</button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Content Item Modal */}
        {showItemModal && (
          <div className={`modal show d-block ${styles.modalOverlay}`} tabIndex="-1">
            <div className="modal-dialog modal-dialog-centered modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">{editingItem ? "Edit Content Item" : "Add Content Item"}</h5>
                  <button type="button" className="btn-close" onClick={() => setShowItemModal(false)}></button>
                </div>
                <form onSubmit={handleSaveContentItem}>
                  <div className={`modal-body ${styles.modalBodyScrollable}`}>
                    {!editingItem && (
                      <div className="mb-4">
                        <label className="form-label fw-semibold">Content Type</label>
                        <div className="d-flex gap-2 flex-wrap">
                          {Object.entries(CONTENT_TYPES).map(([type, cfg]) => (
                            <button key={type} type="button" className={`btn btn-sm ${itemContentType === type ? "btn-primary" : "btn-outline-secondary"}`} onClick={() => setItemContentType(type)}>
                              <ContentTypeIcon type={type} />{cfg.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    {editingItem && <div className="mb-3"><span className={`badge ${CONTENT_TYPES[itemContentType]?.badge}`}>{CONTENT_TYPES[itemContentType]?.label}</span></div>}

                    <div className="mb-3">
                      <label className="form-label fw-semibold">Title *</label>
                      <input type="text" className="form-control" required value={itemForm.title} onChange={e => setItemForm(p => ({ ...p, title: e.target.value }))} />
                    </div>
                    <div className="mb-3">
                      <label className="form-label fw-semibold">Description</label>
                      <textarea className="form-control" rows={2} value={itemForm.description} onChange={e => setItemForm(p => ({ ...p, description: e.target.value }))} />
                    </div>

                    {itemContentType === "video" && (
                      <>
                        <div className="mb-3">
                          <label className="form-label fw-semibold">Video / Embed URL</label>
                          <input type="text" className="form-control" placeholder="https://youtube.com/embed/..." value={itemForm.lessonUrl} onChange={e => setItemForm(p => ({ ...p, lessonUrl: e.target.value }))} />
                        </div>
                        <div className="mb-3">
                          <label className="form-label fw-semibold">Duration</label>
                          <input type="text" className="form-control" placeholder="12:30" value={itemForm.duration} onChange={e => setItemForm(p => ({ ...p, duration: e.target.value }))} />
                        </div>
                      </>
                    )}

                    {itemContentType === "article" && (
                      <div className="mb-3">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <label className="form-label fw-semibold mb-0">Article Content <span className="text-muted small">(Markdown supported)</span></label>
                          <div>
                            <button type="button" className="btn btn-outline-primary btn-sm py-1 px-2" onClick={() => mdInputRef.current?.click()}>
                              <Upload size={13} className="me-1" />Upload .md File
                            </button>
                            <input type="file" ref={mdInputRef} accept=".md,.markdown,.txt" style={{ display: "none" }} onChange={handleMarkdownFileUpload} />
                          </div>
                        </div>
                        <textarea className={`form-control ${styles.articleTextarea}`} rows={12} placeholder={"# Heading\n\n**Bold**, *italic*, `code`, - list"} value={itemForm.articleContent} onChange={e => setItemForm(p => ({ ...p, articleContent: e.target.value }))} />
                      </div>
                    )}

                    {itemContentType === "resource" && (
                      <>
                        <div className="row g-2 mb-3">
                          <div className="col-md-6">
                            <label className="form-label fw-semibold">File Type</label>
                            <select className="form-select" value={itemForm.fileType} onChange={e => setItemForm(p => ({ ...p, fileType: e.target.value }))}>
                              <option>PDF Document</option><option>Source Code ZIP</option><option>Cheat Sheet</option>
                              <option>Presentation Slide</option><option>Reference Link</option><option>DOCX Document</option><option>Other</option>
                            </select>
                          </div>
                          <div className="col-md-6">
                            <label className="form-label fw-semibold">File Size</label>
                            <input type="text" className="form-control" placeholder="2.5 MB" value={itemForm.fileSize} onChange={e => setItemForm(p => ({ ...p, fileSize: e.target.value }))} />
                          </div>
                        </div>
                        <div className="mb-3">
                          <label className="form-label fw-semibold">Upload File</label>
                          <input type="file" className="form-control mb-2" onChange={handleItemFileUpload} />
                          {uploadingItemFile && <div className={styles.progressBar}><div className={`${styles.progressBarFill} ${styles.progressBarInfo}`} style={{ width: `${itemFileProgress}%` }}></div></div>}
                          <label className="form-label fw-semibold">Or External URL</label>
                          <input type="text" className="form-control" placeholder="https://..." value={itemForm.fileUrl} onChange={e => setItemForm(p => ({ ...p, fileUrl: e.target.value }))} />
                          {itemForm.fileUrl && <small className="text-success"><CheckCircle size={12} className="me-1" />URL set</small>}
                        </div>
                      </>
                    )}

                    {itemContentType === "quiz" && (
                      <>
                        <div className="row g-2 mb-3">
                          <div className="col-md-7">
                            <label className="form-label fw-semibold">Quiz Scope</label>
                            <div className="d-flex gap-2">
                              <button type="button" className={`btn btn-sm ${itemForm.quizScope === "module" ? "btn-info text-dark" : "btn-outline-secondary"}`} onClick={() => setItemForm(p => ({ ...p, quizScope: "module" }))}>
                                <HelpCircle size={13} className="me-1" />Module Quiz
                              </button>
                              <button type="button" className={`btn btn-sm ${itemForm.quizScope === "course" ? "btn-dark" : "btn-outline-secondary"}`} onClick={() => setItemForm(p => ({ ...p, quizScope: "course" }))}>
                                <BookOpen size={13} className="me-1" />Course-wide Quiz
                              </button>
                            </div>
                          </div>
                          <div className="col-md-5">
                            <label className="form-label fw-semibold">Pass Mark (%)</label>
                            <input type="number" className="form-control" min="0" max="100" value={itemForm.passMark} onChange={e => setItemForm(p => ({ ...p, passMark: parseInt(e.target.value) || 70 }))} />
                          </div>
                        </div>
                        <div className="mb-3">
                          <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-1">
                            <label className="form-label fw-semibold mb-0">Questions ({itemForm.questions.length})</label>
                            <div className="d-flex gap-1">
                              <button type="button" className="btn btn-outline-secondary btn-sm py-1 px-2" onClick={downloadQuizCSVTemplate} title="Download Quiz Questions CSV Template">
                                <Download size={12} className="me-1" />CSV Template
                              </button>
                              <button type="button" className="btn btn-outline-success btn-sm py-1 px-2" onClick={() => quizCsvInputRef.current?.click()} title="Upload Quiz Questions CSV">
                                <Upload size={12} className="me-1" />Upload Questions CSV
                              </button>
                              <input type="file" ref={quizCsvInputRef} accept=".csv" style={{ display: "none" }} onChange={handleQuizCSVUpload} />
                            </div>
                          </div>
                          {itemForm.questions.map((q, qi) => (
                            <div key={q.id || qi} className="card mb-2 border-0 bg-light">
                              <div className="card-body py-2 px-3">
                                <div className="d-flex justify-content-between">
                                  <div className="flex-grow-1">
                                    <div className="fw-semibold small">Q{qi + 1}: {q.question}</div>
                                    <div className="d-flex flex-wrap gap-1 mt-1">
                                      {q.options.map((opt, oi) => (
                                        <span key={oi} className={`badge ${oi === q.correctIndex ? "bg-success" : "bg-secondary"} ${styles.optionBadge}`}>
                                          {oi === q.correctIndex && <Check size={10} className="me-1" />}{String.fromCharCode(65 + oi)}. {opt}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                  <button type="button" className="btn btn-sm btn-outline-danger py-0 px-1 ms-2" onClick={() => setItemForm(p => ({ ...p, questions: p.questions.filter(q2 => q2.id !== q.id) }))}><Trash2 size={12} /></button>
                                </div>
                              </div>
                            </div>
                          ))}
                          <div className="card border-primary border-opacity-25 mt-2">
                            <div className="card-header py-1 px-3 bg-primary bg-opacity-10 small fw-semibold">Add New Question</div>
                            <div className="card-body py-2 px-3">
                              <input type="text" className="form-control form-control-sm mb-2" placeholder="Question text" value={newQuestion.question} onChange={e => setNewQuestion(p => ({ ...p, question: e.target.value }))} />
                              {newQuestion.options.map((opt, oi) => (
                                <div key={oi} className="input-group input-group-sm mb-1">
                                  <button type="button" className={`btn btn-sm ${newQuestion.correctIndex === oi ? "btn-success" : "btn-outline-secondary"}`} onClick={() => setNewQuestion(p => ({ ...p, correctIndex: oi }))}>
                                    {newQuestion.correctIndex === oi ? <Check size={12} /> : String.fromCharCode(65 + oi)}
                                  </button>
                                  <input type="text" className="form-control form-control-sm" placeholder={`Option ${String.fromCharCode(65 + oi)}`} value={opt} onChange={e => { const opts = [...newQuestion.options]; opts[oi] = e.target.value; setNewQuestion(p => ({ ...p, options: opts })); }} />
                                </div>
                              ))}
                              <button type="button" className="btn btn-sm btn-outline-primary mt-2" onClick={handleAddQuestion}><Plus size={13} className="me-1" />Add Question</button>
                            </div>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn btn-secondary" onClick={() => setShowItemModal(false)}>Cancel</button>
                    <button type="submit" className="btn btn-primary" disabled={savingItem || uploadingItemFile}>{savingItem ? "Saving..." : editingItem ? "Update Item" : "Add Item"}</button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Delete Section */}
        {showDeleteSectionConfirm && (
          <div className={`modal show d-block ${styles.modalOverlay}`} tabIndex="-1">
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header"><h5 className="modal-title text-danger">Delete Section</h5></div>
                <div className="modal-body">Delete <strong>{deletingSection?.sectionTitle}</strong> and all its content?</div>
                <div className="modal-footer">
                  <button className="btn btn-secondary" onClick={() => setShowDeleteSectionConfirm(false)}>Cancel</button>
                  <button className="btn btn-danger" onClick={handleDeleteSectionConfirm}>Delete</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Delete Item */}
        {showDeleteItemConfirm && (
          <div className={`modal show d-block ${styles.modalOverlay}`} tabIndex="-1">
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header"><h5 className="modal-title text-danger">Delete Content Item</h5></div>
                <div className="modal-body">Delete this content item? This cannot be undone.</div>
                <div className="modal-footer">
                  <button className="btn btn-secondary" onClick={() => setShowDeleteItemConfirm(false)}>Cancel</button>
                  <button className="btn btn-danger" onClick={handleConfirmDeleteItem}>Delete</button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── CATALOG VIEW ─────────────────────────────────────────────────────────────
  const publishedCount = courses.filter(c => c.isPublished).length;
  const draftCount = courses.filter(c => !c.isPublished).length;

  return (
    <div className={styles.courseManagement}>
      <PageHeader
        icon={BookOpen}
        title="Course Catalog & Content Manager"
        subtitle="Manage courses with sections — videos, articles, resources & quizzes in one place."
        badge={`${courses.length} Courses`}
        actions={
          <>
            <button className={styles.secondaryBtn} onClick={() => setShowCSVUploadModal(true)}><Upload size={16} /> Import CSV</button>
            <button className={styles.secondaryBtn} onClick={exportCourses}><Download size={16} /> Export CSV</button>
            <button className={styles.primaryBtn} onClick={() => { setCourseForm({ title: "", description: "", category: "", difficulty: "beginner", duration: "", price: "", instructor: "", tags: [], imageUrl: "", isPublished: false, learningOutcomes: ["", "", "", ""], prerequisites: ["", ""] }); setShowCourseModal(true); }}><Plus size={16} />Add Course</button>
          </>
        }
      />

      {/* Sub-Tabs Navigation */}
      <div className={styles.subTabs}>
        <button
          className={`${styles.subTab} ${activeSubTab === "courses" ? styles.activeSubTab : ""}`}
          onClick={() => setActiveSubTab("courses")}
        >
          <BookOpen size={16} />
          Manage Courses ({courses.length})
        </button>
        <button
          className={`${styles.subTab} ${activeSubTab === "quizRequests" ? styles.activeSubTab : ""}`}
          onClick={() => {
            setActiveSubTab("quizRequests");
            fetchQuizRequests();
          }}
        >
          <AlertCircle size={16} />
          Quiz Reset Requests ({quizRequests.length})
        </button>
      </div>

      {activeSubTab === "courses" && (
        <>
          <div className={styles.statsRow}>
            <div className={styles.statCard}><div className={styles.statNumber}>{courses.length}</div><div className={styles.statLabel}>Total Courses</div></div>
            <div className={styles.statCard}><div className={`${styles.statNumber} text-success`}>{publishedCount}</div><div className={styles.statLabel}>Published</div></div>
            <div className={styles.statCard}><div className={`${styles.statNumber} text-warning`}>{draftCount}</div><div className={styles.statLabel}>Drafts</div></div>
          </div>

          <div className={styles.filters}>
            <div className={styles.searchBox}>
              <Search size={18} className={styles.searchIcon} />
              <input type="text" placeholder="Search courses..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className={styles.searchInput} />
            </div>
            <div className={styles.filterGroup}>
              <select value={filterCategory} onChange={e => setFilterCategory(e.target.value)} className={styles.filterSelect}>
                <option value="all">All Categories</option>
                {categories.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className={styles.filterSelect}>
                <option value="all">All Status</option>
                <option value="published">Published</option>
                <option value="draft">Draft</option>
              </select>
            </div>
          </div>

          <div className={styles.courseGrid}>
            {filteredCourses.length === 0 ? (
              <div className={styles.emptyState}>
                <BookOpen size={48} className="text-muted mb-3" /><h5>No courses found</h5>
                <p className="text-muted">Adjust filters or create a new course.</p>
              </div>
            ) : filteredCourses.map(course => (
              <div key={course.id} className={styles.courseCard}>
                {course.imageUrl && <img src={course.imageUrl} alt={course.title} className={styles.courseThumb} />}
                <div className={styles.courseCardBody}>
                  <div className={styles.courseHeader}>
                    <h5 className={styles.courseTitle}>{course.title}</h5>
                    <button className="btn btn-sm btn-outline-danger py-0 px-1" onClick={() => { setCourseToDelete(course); setShowDeleteModal(true); }}><Trash2 size={13} /></button>
                  </div>
                  <div className="d-flex gap-1 mb-2">
                    <span className={`badge ${getStatColor(course.isPublished)}`}>{course.isPublished ? "Published" : "Draft"}</span>
                    <span className={`badge ${getDiffColor(course.difficulty)}`}>{course.difficulty}</span>
                  </div>
                  <p className={styles.courseDescription}>{course.description?.substring(0, 90)}{course.description?.length > 90 ? "..." : ""}</p>
                  <div className={styles.courseMeta}>
                    <div className={styles.metaItem}><Tag size={14} className="me-1" /><span>{course.category}</span></div>
                    <div className={styles.metaItem}><Clock size={14} className="me-1" /><span>{course.duration || "N/A"}</span></div>
                    {course.price && <div className={styles.metaItem}><span>{String(course.price).toLowerCase().includes('free') ? course.price : `₹${String(course.price).replace(/[\$₹]/g, '').trim()}`}</span></div>}
                  </div>
                  <div className={styles.courseFooter}>
                    <div className={styles.courseInfo}>{course.instructor && <span className="text-muted small">by {course.instructor}</span>}</div>
                    <button className={styles.manageContentBtn} onClick={() => handleOpenWorkspace(course)}>
                      <Upload size={14} className="me-1" />Manage &amp; Upload Content
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Quiz Reset Requests Tab Content */}
      {activeSubTab === "quizRequests" && (
        <div className={styles.card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem', flexWrap: 'wrap', gap: '1rem' }}>
            <div>
              <h4 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 600, color: 'var(--text-primary, #1e293b)' }}>Student Quiz Reset Requests</h4>
              <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.85rem', color: 'var(--text-secondary, #64748b)' }}>
                Review and approve student requests for additional course quiz attempts.
              </p>
            </div>
            <button className={styles.exportBtn} onClick={fetchQuizRequests} disabled={loadingRequests}>
              {loadingRequests ? "Refreshing..." : "↻ Refresh List"}
            </button>
          </div>

          {requestsStatus.message && (
            <div className={`alert ${requestsStatus.type === "error" ? "alert-danger" : "alert-success"} mb-3 small`}>
              {requestsStatus.message}
            </div>
          )}

          {loadingRequests ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary spinner-border-sm me-2" role="status"></div>
              <span className="text-muted">Fetching quiz requests...</span>
            </div>
          ) : quizRequests.length === 0 ? (
            <div className={styles.emptyState} style={{ padding: '3rem 1rem' }}>
              <AlertCircle size={44} className="text-muted mb-2" />
              <p className="fw-semibold text-secondary mb-1">No Quiz Reset Requests Found</p>
              <p className="small text-muted mb-0">Student requests to increase quiz attempt limits will appear here.</p>
            </div>
          ) : (
            <div className={styles.requestsTableContainer}>
              <table className={styles.requestsTable}>
                <thead>
                  <tr>
                    <th>Student</th>
                    <th>Quiz / Course</th>
                    <th>Requested Limit</th>
                    <th>Status</th>
                    <th style={{ textAlign: 'right' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {quizRequests.map(req => (
                    <tr key={req.id}>
                      <td>
                        <div className="fw-semibold">{req.userEmail || "Unknown Email"}</div>
                        <div className="text-muted small">ID: {req.userId || "N/A"}</div>
                      </td>
                      <td>
                        <div className="fw-semibold">{req.moduleTitle || req.courseTitle || "Course Quiz"}</div>
                        <div className="text-muted small">ID: {req.moduleId || req.courseId || "N/A"}</div>
                      </td>
                      <td>
                        <span className="fw-bold text-primary">{req.allowedAttempts || 4} attempts</span>
                      </td>
                      <td>
                        <span className={`${styles.statusBadge} ${
                          req.status === 'approved' 
                            ? styles.statusApproved 
                            : req.status === 'rejected' 
                              ? styles.statusRejected 
                              : styles.statusPending
                        }`}>
                          {req.status || "pending"}
                        </span>
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        {req.status === 'pending' ? (
                          <div style={{ display: 'flex', gap: '0.35rem', justifyContent: 'flex-end' }}>
                            <button 
                              className={`${styles.requestActionBtn} ${styles.approveBtn}`}
                              onClick={() => handleApproveRequest(req.id)}
                            >
                              <CheckCircle size={14} /> Approve
                            </button>
                            <button 
                              className={`${styles.requestActionBtn} ${styles.rejectBtn}`}
                              onClick={() => handleRejectRequest(req.id)}
                            >
                              <XCircle size={14} /> Reject
                            </button>
                          </div>
                        ) : (
                          <span className="text-muted small fst-italic">Processed</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Create Course Modal */}
      {showCourseModal && (
        <div className={`modal show d-block ${styles.modalOverlay}`} tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Create New Course</h5>
                <button type="button" className="btn-close" onClick={() => setShowCourseModal(false)}></button>
              </div>
              <form onSubmit={handleSaveNewCourse}>
                <div className="modal-body">
                  <div className="row g-3">
                    <div className="col-md-8">
                      <label className="form-label fw-semibold">Course Title *</label>
                      <input type="text" className="form-control" required value={courseForm.title} onChange={e => setCourseForm(p => ({ ...p, title: e.target.value }))} />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label fw-semibold">Instructor</label>
                      <input type="text" className="form-control" value={courseForm.instructor} onChange={e => setCourseForm(p => ({ ...p, instructor: e.target.value }))} />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label fw-semibold">Category *</label>
                      <select className="form-select" required value={courseForm.category} onChange={e => setCourseForm(p => ({ ...p, category: e.target.value }))}>
                        <option value="">Select Category</option>
                        {categories.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label fw-semibold">Difficulty</label>
                      <select className="form-select" value={courseForm.difficulty} onChange={e => setCourseForm(p => ({ ...p, difficulty: e.target.value }))}>
                        {difficulties.map(d => <option key={d} value={d}>{d.toUpperCase()}</option>)}
                      </select>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label fw-semibold">Price</label>
                      <input type="text" className="form-control" placeholder="₹199" value={courseForm.price} onChange={e => setCourseForm(p => ({ ...p, price: e.target.value }))} />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label fw-semibold">Silicon-Point Value</label>
                      <input type="number" min="0" className="form-control" placeholder="50" value={courseForm.siliconPoints} onChange={e => setCourseForm(p => ({ ...p, siliconPoints: e.target.value }))} />
                    </div>
                    <div className="col-12">
                      <label className="form-label fw-semibold">Description</label>
                      <textarea className="form-control" rows={3} value={courseForm.description} onChange={e => setCourseForm(p => ({ ...p, description: e.target.value }))} />
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowCourseModal(false)}>Cancel</button>
                  <button type="submit" className="btn btn-primary">Create &amp; Open Workspace</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Course */}
      {showDeleteModal && (
        <div className={`modal show d-block ${styles.modalOverlay}`} tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header"><h5 className="modal-title text-danger">Delete Course</h5></div>
              <div className="modal-body">Delete <strong>{courseToDelete?.title}</strong>? Cannot be undone.</div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowDeleteModal(false)}>Cancel</button>
                <button className="btn btn-danger" onClick={handleDeleteCourse}>Delete</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Course CSV Import Modal */}
      {showCSVUploadModal && (
        <div className={`modal show d-block ${styles.modalOverlay}`} tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title d-flex align-items-center gap-2">
                  <Upload size={20} className="text-primary" />
                  Import Courses from CSV
                </h5>
                <button type="button" className="btn-close" onClick={() => setShowCSVUploadModal(false)}></button>
              </div>
              <form onSubmit={handleCourseCSVUpload}>
                <div className="modal-body">
                  <p className="text-muted small mb-3">
                    Upload a CSV file containing course details. Format columns according to the template below.
                  </p>

                  <div className="d-flex justify-content-between align-items-center mb-3 p-2 bg-light border rounded">
                    <span className="small text-secondary fw-semibold">Need template format?</span>
                    <button type="button" className="btn btn-sm btn-outline-primary" onClick={downloadCourseCSVTemplate}>
                      <Download size={14} className="me-1" /> Download CSV Template
                    </button>
                  </div>

                  {csvUploadStatus.message && (
                    <div className={`alert ${csvUploadStatus.type === "error" ? "alert-danger" : csvUploadStatus.type === "warning" ? "alert-warning" : "alert-success"} mb-3 small`}>
                      {csvUploadStatus.message}
                    </div>
                  )}

                  <div className="mb-3">
                    <label className="form-label fw-semibold">Select CSV File *</label>
                    <input
                      type="file"
                      accept=".csv"
                      className="form-control"
                      required
                      onChange={e => setCourseCsvFile(e.target.files[0])}
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShowCSVUploadModal(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary btn-sm" disabled={uploadingCourseCSV || !courseCsvFile}>
                    {uploadingCourseCSV ? "Uploading Courses..." : "Import Courses"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CourseManagement;
